
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ReadingHistoryArticle } from '@/hooks/useReadingHistory';
import { BookOpen, Clock, User, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';

interface ReadingHistoryListProps {
  articles: ReadingHistoryArticle[];
  isLoading: boolean;
  error?: Error | null;
}

const ReadingHistoryList: React.FC<ReadingHistoryListProps> = ({ 
  articles, 
  isLoading,
  error 
}) => {
  const navigate = useNavigate();

  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-12 w-12 text-destructive" />}
        title="Failed to load reading history"
        description="There was a problem loading your reading history. Please try again later."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row p-4 gap-4">
                <Skeleton className="h-24 w-24 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <EmptyState
        icon={<BookOpen className="h-12 w-12 text-muted-foreground" />}
        title="No reading history yet"
        description="Articles you read will appear here."
      />
    );
  }

  return (
    <div className="space-y-4">
      {articles.map((article) => (
        <Card key={article.id} className="overflow-hidden hover:shadow-md transition-shadow">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {article.coverImage && (
                <div className="w-full md:w-1/4 h-40 md:h-auto bg-muted">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 p-4 space-y-3">
                <h3 className="text-lg font-medium line-clamp-2">{article.title}</h3>
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span>{article.author.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{article.date}</span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Reading progress</span>
                    <span className="font-medium">{article.progress}%</span>
                  </div>
                  <Progress value={article.progress} className="h-2" />
                </div>
                
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/article/${article.id}`)}
                  >
                    {article.isComplete ? 'Read again' : 'Continue reading'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ReadingHistoryList;
